import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { PlusIcon, Trash2Icon, ChevronUpIcon, ChevronDownIcon, GripVerticalIcon, PencilIcon, ArchiveIcon, RotateCcwIcon } from 'lucide-react';
import { useJournal } from '../../context/JournalContext';
import { Tag, TagCategory } from '../../lib/types';
import { Button } from '../ui/Button';
import { Input } from '../ui/Field';
import { cn } from '../../lib/cn';

/** The three strategy categories always stay first, in this order. */
const LOCKED_COUNT = 3;
function CategoryNumber({
  index
}: {
  index: number;
}) {
  return <span className="flex shrink-0 items-center justify-center text-[12px] font-semibold" style={{
    width: 22,
    height: 22,
    borderRadius: '50%',
    background: '#f0f1f3',
    color: '#4f5560'
  }} data-id="element-510" data-mp="11s0wzl49196b">
      {index + 1}
    </span>;
}
function TagRow({
  tag
}: {
  tag: Tag;
}) {
  const {
    state,
    categoriesSorted,
    updateTag,
    deleteTag,
    reorderTags,
    tagUsageCount
  } = useJournal();
  const [editing, setEditing] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [over, setOver] = useState(false);
  const [label, setLabel] = useState(tag.label);
  const [categoryId, setCategoryId] = useState(tag.categoryId);
  const siblings = state.tags.filter(item => item.categoryId === tag.categoryId && !item.archived).sort((a, b) => a.order - b.order);
  const idx = siblings.findIndex(item => item.id === tag.id);
  const usage = tagUsageCount(tag.id);

  // Drag & drop reordering, Notion style: hold, drag, drop.
  const dropFrom = (fromId: string) => {
    const ids = siblings.map(item => item.id);
    const from = ids.indexOf(fromId);
    if (from < 0 || idx < 0 || from === idx) return;
    ids.splice(from, 1);
    ids.splice(idx, 0, fromId);
    reorderTags(tag.categoryId, ids);
  };
  const save = () => {
    // The tag id never changes, so renaming keeps every historical statistic.
    updateTag(tag.id, {
      label: label.trim() || tag.label,
      categoryId
    });
    setEditing(false);
  };
  if (editing) {
    return <div className="rounded-xl border border-line bg-surface/60 p-3" data-id="element-511" data-mp="1mevwvfpw1490">
        <Input value={label} onChange={e => setLabel(e.target.value)} onKeyDown={e => e.key === 'Enter' && save()} autoFocus data-id="element-512" data-mp="1rakql2f87qxe" />
        <label className="mt-2.5 block text-[11px] font-medium text-ink-soft" data-id="element-513" data-mp="1ckvhml0w6imk">
          Categoría
          <select value={categoryId} onChange={event => setCategoryId(event.target.value)} className="mt-1 h-9 w-full rounded-lg border border-line bg-white px-2.5 text-[13px] focus:outline-none focus:ring-2 focus:ring-accent/25" data-id="element-514" data-mp="1hjzvhoyrh4j8">
            {categoriesSorted.map((category, index) => <option key={category.id} value={category.id} data-id="element-515" data-mp="ymf2q6svlltc" data-mpi={index}>{index + 1}. {category.name}</option>)}
          </select>
        </label>
        <div className="mt-3 flex justify-end gap-2" data-id="element-516" data-mp="2o2xz9k4wyrue">
          <Button size="sm" variant="ghost" onClick={() => setEditing(false)} data-id="element-517" data-mp="2t1hp3vmmons1">Cancelar</Button>
          <Button size="sm" variant="primary" onClick={save} data-id="element-518" data-mp="hdcumd5bt2hg">Guardar</Button>
        </div>
      </div>;
  }
  return <motion.div layout transition={{
    duration: 0.18
  }} draggable={!tag.archived} onDragStart={event => {
    const native = event as unknown as React.DragEvent;
    native.dataTransfer?.setData('text/tag-id', tag.id);
  }} onDragOver={event => {
    const native = event as unknown as React.DragEvent;
    if (native.dataTransfer?.types.includes('text/tag-id')) {
      native.preventDefault();
      setOver(true);
    }
  }} onDragLeave={() => setOver(false)} onDrop={event => {
    const native = event as unknown as React.DragEvent;
    const fromId = native.dataTransfer?.getData('text/tag-id');
    if (fromId && fromId !== tag.id) {
      native.preventDefault();
      dropFrom(fromId);
    }
    setOver(false);
  }} className={cn('rounded-lg border transition-colors duration-150', !tag.archived && 'cursor-grab active:cursor-grabbing', over && 'ring-2 ring-ink/20')} style={{
    background: '#f4f5f7',
    borderColor: '#e2e4e8'
  }} data-id="element-519" data-mp="cfum2nv80p9z">
      <div className="group flex items-center gap-2 px-2.5 py-2" data-id="element-520" data-mp="27wcccoqwon0w">
        <GripVerticalIcon className="h-3.5 w-3.5 shrink-0 text-ink-soft/50" data-id="element-521" data-mp="27wukojquerp6" />
        <span className="flex-1 truncate text-sm" style={{
        color: '#4f5560'
      }} data-id="element-522" data-mp="2bwczcxs2m6jk">{tag.label}</span>
        {usage > 0 && <span className="rounded border px-1.5 py-0.5 text-[10px] font-medium" style={{
        background: '#ffffff',
        borderColor: '#e2e4e8',
        color: '#4f5560'
      }} data-id="element-523" data-mp="1f5atodo089cm">
            {usage} {usage === 1 ? 'operación' : 'operaciones'}
          </span>}
        {tag.archived && <span className="rounded bg-white px-1.5 py-0.5 text-[10px] font-medium text-ink-soft" data-id="element-524" data-mp="1sztxmtjljkx1">Archivada</span>}
        <div className="flex items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100" data-id="element-525" data-mp="wgeap5y2539p">
          <button onClick={() => setEditing(true)} className="flex h-7 w-7 items-center justify-center rounded-md text-ink-soft hover:bg-white hover:text-ink" aria-label="Editar etiqueta" data-id="element-526" data-mp="10gy7g8epy4vb">
            <PencilIcon className="h-3.5 w-3.5" data-id="element-527" data-mp="o16uhimmcdvi" />
          </button>
          <button onClick={() => updateTag(tag.id, {
          archived: !tag.archived
        })} className="flex h-7 w-7 items-center justify-center rounded-md text-ink-soft hover:bg-white hover:text-ink" aria-label={tag.archived ? 'Restaurar etiqueta' : 'Archivar etiqueta'} data-id="element-528" data-mp="jxscimf4dnff">
            {tag.archived ? <RotateCcwIcon className="h-3.5 w-3.5" data-id="element-529" data-mp="58m6aehv6wy1" /> : <ArchiveIcon className="h-3.5 w-3.5" data-id="element-530" data-mp="57ksw3ddlyd5" />}
          </button>
          <button onClick={() => usage > 0 ? setConfirmDelete(true) : deleteTag(tag.id)} className="flex h-7 w-7 items-center justify-center rounded-md text-ink-soft hover:bg-neg-soft hover:text-neg" aria-label="Eliminar etiqueta" data-id="element-531" data-mp="auc1dtqyyqo">
            <Trash2Icon className="h-3.5 w-3.5" data-id="element-532" data-mp="3ih8r5g3r5eqn" />
          </button>
        </div>
      </div>

      {confirmDelete && <div className="border-t bg-white px-3 py-2.5" style={{
      borderColor: '#e2e4e8'
    }} data-id="element-533" data-mp="38r0g69km3hxv">
          <p className="text-[12px] leading-relaxed text-ink-soft" data-id="element-534" data-mp="3dszjxkk9kgvc">
            Esta etiqueta tiene operaciones asociadas. Puedes archivarla para conservar el histórico o eliminarla definitivamente.
          </p>
          <div className="mt-2 flex flex-wrap justify-end gap-2" data-id="element-535" data-mp="33th157g3b14v">
            <Button size="sm" variant="ghost" onClick={() => setConfirmDelete(false)} data-id="element-536" data-mp="3rjunchbzu5rj">Cancelar</Button>
            <Button size="sm" variant="secondary" onClick={() => {
          updateTag(tag.id, {
            archived: true
          });
          setConfirmDelete(false);
        }} data-id="element-537" data-mp="b70fwy3uahv">
              Archivar
            </Button>
            <Button size="sm" variant="danger" onClick={() => {
          deleteTag(tag.id);
          setConfirmDelete(false);
        }} data-id="element-538" data-mp="3mrb8zi5hf6lo">
              Eliminar definitivamente
            </Button>
          </div>
        </div>}
    </motion.div>;
}
function CategoryBlock({
  category,
  index
}: {
  category: TagCategory;
  index: number;
}) {
  const {
    state,
    addTag,
    renameCategory,
    updateCategory,
    deleteCategory,
    categoriesSorted,
    reorderCategories
  } = useJournal();
  const tags = state.tags.filter(tag => tag.categoryId === category.id).sort((a, b) => a.order - b.order);
  const [newTag, setNewTag] = useState('');
  const [editingName, setEditingName] = useState(false);
  const [name, setName] = useState(category.name);
  const [timeframe, setTimeframe] = useState(category.timeframe ?? '');

  // Dirección, Zona and Entrada are pinned to positions 1-3.
  const locked = index < LOCKED_COUNT;
  const canMoveUp = !locked && index > LOCKED_COUNT;
  const canMoveDown = !locked && index < categoriesSorted.length - 1;
  const moveCat = (dir: -1 | 1) => {
    const target = index + dir;
    if (target < LOCKED_COUNT || target >= categoriesSorted.length) return;
    const ids = categoriesSorted.map(item => item.id);
    [ids[index], ids[target]] = [ids[target], ids[index]];
    reorderCategories(ids);
  };
  const add = () => {
    if (!newTag.trim()) return;
    addTag(category.id, newTag.trim());
    setNewTag('');
  };
  return <section className="rounded-2xl border border-line bg-white p-4 shadow-card" data-id="element-539" data-mp="13m9w193et7b8">
      <div className="mb-3 flex flex-wrap items-center gap-2" data-id="element-540" data-mp="18jrug5jsw44z">
        <CategoryNumber index={index} data-id="element-541" data-mp="18igs8a6074fz" />
        {editingName ? <Input value={name} autoFocus onChange={e => setName(e.target.value)} onBlur={() => {
        renameCategory(category.id, name.trim() || category.name);
        setEditingName(false);
      }} onKeyDown={e => {
        if (e.key === 'Enter') {
          renameCategory(category.id, name.trim() || category.name);
          setEditingName(false);
        }
      }} className="h-8 max-w-[220px]" data-id="element-542" data-mp="1dbtcy97kar3g" /> : <button onClick={() => setEditingName(true)} className="text-[14px] font-semibold tracking-tight" style={{
        color: '#24272c'
      }} data-id="element-543" data-mp="3t40hosiihrmk">
            {index + 1}. {category.name}
          </button>}

        <input value={timeframe} onChange={event => setTimeframe(event.target.value)} onBlur={() => updateCategory(category.id, {
        timeframe: timeframe.trim() || undefined
      })} placeholder="Temporalidad" className="h-8 w-[120px] rounded-lg border border-line px-2 text-[12px] focus:outline-none focus:ring-2 focus:ring-accent/25" aria-label={`Temporalidad de ${category.name}`} data-id="element-544" data-mp="2aew9i5fe8gjt" />

        <div className="flex h-8 items-center rounded-lg border border-line p-0.5" data-id="element-545" data-mp="2u46mhbnr8ne8">
          {([{
          label: 'Única',
          value: false
        }, {
          label: 'Múltiple',
          value: true
        }] as const).map(option => <button key={option.label} onClick={() => updateCategory(category.id, {
          multiple: option.value
        })} aria-pressed={!!category.multiple === option.value} className={cn('h-7 rounded-md px-2 text-[11px] font-medium transition-colors', !!category.multiple === option.value ? 'bg-ink text-white' : 'text-ink-soft hover:text-ink')} data-id="element-546" data-mp="2k4zez53pbmz1">
              {option.label}
            </button>)}
        </div>

        <div className="ml-auto flex items-center gap-2" data-id="element-547" data-mp="1hik2h7dou9kn">
          <span className="text-[12px] text-ink-soft" data-id="element-548" data-mp="1hgz93l340wod">
            {tags.length} {tags.length === 1 ? 'etiqueta' : 'etiquetas'}
          </span>
          <button onClick={() => moveCat(-1)} disabled={!canMoveUp} className="flex h-7 w-7 items-center justify-center rounded-md text-ink-soft hover:bg-surface disabled:opacity-25" aria-label="Subir categoría" data-id="element-549" data-mp="1qb432ddjio84">
            <ChevronUpIcon className="h-4 w-4" data-id="element-550" data-mp="208i537ypvagw" />
          </button>
          <button onClick={() => moveCat(1)} disabled={!canMoveDown} className="flex h-7 w-7 items-center justify-center rounded-md text-ink-soft hover:bg-surface disabled:opacity-25" aria-label="Bajar categoría" data-id="element-551" data-mp="207775orycwxp">
            <ChevronDownIcon className="h-4 w-4" data-id="element-552" data-mp="2him5sjzbsyy9" />
          </button>
          <button onClick={() => deleteCategory(category.id)} disabled={locked} className="flex h-7 w-7 items-center justify-center rounded-md text-ink-soft hover:bg-neg-soft hover:text-neg disabled:opacity-25" aria-label="Eliminar categoría" data-id="element-553" data-mp="2hkgqpyalkr2c">
            <Trash2Icon className="h-4 w-4" data-id="element-554" data-mp="2cpklfwgco77a" />
          </button>
        </div>
      </div>

      <div className="flex flex-col gap-1.5" data-id="element-555" data-mp="1yqkyct8hk0d8">
        {tags.map(tag => <TagRow key={tag.id} tag={tag} data-id="element-556" data-mp="22uiwb0m2r54p" />)}
        {tags.length === 0 && <p className="px-1 py-2 text-[13px] text-ink-soft" data-id="element-557" data-mp="1ou851wguroj0">Todavía no hay etiquetas en esta categoría.</p>}
      </div>

      <div className="mt-3 rounded-xl border border-dashed border-line p-3" data-id="element-558" data-mp="1ttkqqdvzmpk0">
        <div className="flex gap-2" data-id="element-559" data-mp="3j7g4y2joqqbi">
          <Input placeholder="Nueva etiqueta…" value={newTag} onChange={e => setNewTag(e.target.value)} onKeyDown={e => e.key === 'Enter' && add()} className="h-9" data-id="element-560" data-mp="3j8iv2896ooqd" />
          <Button size="sm" variant="primary" onClick={add} className="h-9" data-id="element-561" data-mp="3hqty3cjs43to">
            <PlusIcon className="h-4 w-4" data-id="element-562" data-mp="3hswymqik5fcn" />
          </Button>
        </div>
      </div>
    </section>;
}
export function TagManager() {
  const {
    categoriesSorted,
    addCategory
  } = useJournal();
  const [newCat, setNewCat] = useState('');
  const add = () => {
    if (!newCat.trim()) return;
    addCategory(newCat.trim());
    setNewCat('');
  };
  return <div className="mx-auto max-w-3xl" data-id="element-563" data-mp="4cp8wqaw2nvz">
      <div className="mb-5" data-id="element-564" data-mp="4dqtbldsomqd">
        <h1 className="text-xl font-semibold tracking-tight" data-id="element-565" data-mp="3vptn4lb7ekqd">Etiquetas</h1>
        <p className="mt-0.5 text-[13px] leading-relaxed text-ink-soft" data-id="element-566" data-mp="3vnz1f4s3yol8">
          El flujo de la estrategia se lee de arriba abajo: 1. Dirección → 2. Zona → 3. Entrada. Los identificadores
          internos se mantienen, así que renombrar una etiqueta nunca rompe tus estadísticas.
        </p>
      </div>

      <div className="mb-5 flex gap-2 rounded-2xl border border-line bg-white p-3 shadow-card" data-id="element-567" data-mp="vj5ymmw3m21r">
        <Input placeholder="Nueva categoría (p. ej. Gestión, Sesión, Emoción…)" value={newCat} onChange={e => setNewCat(e.target.value)} onKeyDown={e => e.key === 'Enter' && add()} data-id="element-568" data-mp="zkrhqpz87cke" />
        <Button variant="primary" onClick={add} data-id="element-569" data-mp="19j76wnep0br9">
          <PlusIcon className="h-4 w-4" data-id="element-570" data-mp="19gkqdy9tqlww" />
          Añadir categoría
        </Button>
      </div>

      <div className="flex flex-col gap-4" data-id="element-571" data-mp="1efxlf6aumzak">
        {categoriesSorted.map((category, index) => <CategoryBlock key={category.id} category={category} index={index} data-id="element-572" data-mp="1ihizguz4djmc" data-mpi={index} />)}
        {categoriesSorted.length === 0 && <div className="rounded-2xl border border-dashed border-line py-14 text-center" data-id="element-573" data-mp="1s9ycrezh1k0w">
            <p className="text-sm font-medium text-ink" data-id="element-574" data-mp="1x8rqn8io7d1l">Sin categorías</p>
            <p className="mt-1 text-[13px] text-ink-soft" data-id="element-575" data-mp="1x6osg17d6dls">Crea tu primera categoría para empezar a etiquetar operaciones.</p>
          </div>}
      </div>
    </div>;
}